import { useMemo } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import db from '@/db/db';
import type { OrganizationClientsBase } from './useOrganizationClients';

export function useRecentDocumentsData(
  organizationId: number | null,
  { organizationClients, clientIdSet, clientIdsKey, clientsForTable }: OrganizationClientsBase,
) {
  // sortBy loads all matching docs into memory before slicing. Bounded by the
  // 100-client cap and 30-day filter, so manageable for now. A compound index
  // on [client_id+updated_at] would allow a native .reverse().limit(50) query.
  const recentDocuments = useLiveQuery(
    async () => {
      if (!organizationClients) return undefined;
      if (clientIdSet.size === 0) return [];
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      return db.documents
        .where('client_id')
        .anyOf([...clientIdSet])
        .and(doc => doc.super_form !== undefined && new Date(doc.updated_at) >= thirtyDaysAgo)
        .sortBy('updated_at')
        .then(docs => docs.reverse().slice(0, 50));
    },
    [clientIdsKey]
  );

  const templates = useLiveQuery(
    async () => {
      if (!organizationId) return undefined;
      return db.documentTemplates
        .where('organization_id')
        .equals(organizationId)
        .toArray();
    },
    [organizationId]
  );

  const templatesMap = useMemo(() => {
    if (!templates) return undefined;
    return new Map(templates.map(t => [t.id, t]));
  }, [templates]);

  const clientsMap = useMemo(() => {
    if (!clientsForTable) return undefined;
    return new Map(clientsForTable.map(c => [c.id, c]));
  }, [clientsForTable]);

  return {
    recentDocuments,
    clientsMap,
    templatesMap,
  };
}
